import { skipToken } from "@reduxjs/toolkit/query";
import { automationArtifactIdSchema } from "@repo/app-types/entities/automationArtifact";
import { asPlaybookId } from "@repo/app-types/entities/playbook";
import { asTeammateId } from "@repo/app-types/entities/teammate";
import { AutomationArtifactContent } from "@repo/ui/automations/automationArtifactContent";
import { formatRunTime } from "@repo/ui/automations/automationPresentation";
import { resolveArtifactSentAt } from "@repo/ui/automations/automationTables";
import { Loader, LoaderStyle } from "@repo/ui/Loader/loader";
import { PageHeader } from "@repo/ui/Navigation/pageHeader";
import { PageWidth } from "@repo/ui/Navigation/pageWidth";
import { GradingReferenceProvider } from "@repo/ui/structuredContent/gradingReferenceContext";
import type { FC } from "react";
import { Navigate, useParams } from "react-router";
import { useMyAutomationResultByIdQuery } from "../../api/entities/automationArtifact.js";
import { appPaths } from "../../app/appPaths.js";
import { PageBody, PageLayout } from "../../components/layout/pageLayout.js";
import { GradingReferenceLinkAdapter } from "./gradingReferenceLinkAdapter.js";

/**
 * The focused teammate-facing view of a single automation result. Reached from the
 * Notifications list and, via the deep-link redirect, from an email or
 * Slack/Teams link. It resolves the result from its id through the
 * recipient-scoped endpoint, so a teammate can only ever open a result
 * delivered to them; anything else redirects back to their Notifications list.
 *
 * It's a standalone page (rather than a tab of the teammate page) so it renders
 * as its own titled page with a breadcrumb, without the teammate tab header.
 */
export const TrainingTeammatePageAutomationResultDetail: FC = () => {
  const {
    playbookId: playbookIdParam,
    teammateId: teammateIdParam,
    artifactId: artifactIdParam,
  } = useParams();
  if (!playbookIdParam || !teammateIdParam || !artifactIdParam) {
    throw new Error("Missing automation result route params.");
  }

  const playbookId = asPlaybookId(playbookIdParam);
  const teammateId = asTeammateId(teammateIdParam);
  const parsedArtifactId =
    automationArtifactIdSchema.safeParse(artifactIdParam);

  const { data: artifact, isLoading } = useMyAutomationResultByIdQuery(
    parsedArtifactId.success
      ? { automationArtifactId: parsedArtifactId.data, playbookId }
      : skipToken,
  );

  const resultsHref = appPaths.teammateAutomationResults.build({
    playbookId,
    teammateId,
  });

  if (!parsedArtifactId.success) {
    return <Navigate to={resultsHref} replace />;
  }

  if (isLoading) {
    return (
      <PageLayout>
        <PageHeader isLoading width={PageWidth.Standard} />
        <PageBody width={PageWidth.Standard}>
          <div className="flex items-center justify-center py-12">
            <Loader style={LoaderStyle.ZOOMIES} />
          </div>
        </PageBody>
      </PageLayout>
    );
  }

  if (!artifact) {
    return <Navigate to={resultsHref} replace />;
  }

  const receivedAt = resolveArtifactSentAt(artifact) ?? artifact.created_at;

  return (
    <PageLayout>
      <PageHeader
        width={PageWidth.Standard}
        eyebrow={formatRunTime(receivedAt)}
        title={artifact.title}
      />
      <PageBody width={PageWidth.Standard}>
        <article className="w-full rounded-lg border border-border-light bg-white p-6">
          <GradingReferenceProvider value={GradingReferenceLinkAdapter}>
            <AutomationArtifactContent artifact={artifact} />
          </GradingReferenceProvider>
        </article>
      </PageBody>
    </PageLayout>
  );
};
